import React from 'react';
import { useDispatch } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { Grid, makeStyles } from '@material-ui/core';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { DialogComponent } from 'common/components';
import messages from 'common/messages/messages';
import inputLabels from 'common/messages/inputLabels';
import { getRequiredErrorMessage } from 'common/utils/validation';
import { useRenderIntlMessage } from 'common/hooks/useRenderIntlMessage';
import { useUpdateProfilePinMutation } from 'memberPortalModules/MemberProfile/state/profile/memberPortileProfile.api';
import { IFormEditMemberPortalPinValues } from 'memberPortalModules/MemberProfile/interface';
import { enqueueErrorNotification } from 'common/state/notifications/actions';
import validationMessages from '../../../../common/messages/validation';
import PinField from '../../../../common/components/PinField/PinFields';

interface IEditCredentialsModalProps {
  onClose: () => void;
}

const validationSchema = yup.object({
  currentPinCode: yup
    .string()
    .length(4, () => validationMessages.pinLength)
    .matches(/^[0-9]*$/, () => validationMessages.pinAllowedDigits)
    .required(getRequiredErrorMessage),
  newPinCode: yup
    .string()
    .length(4, () => validationMessages.pinLength)
    .matches(/^[0-9]*$/, () => validationMessages.pinAllowedDigits)
    .required(getRequiredErrorMessage),
});

const initialValues = {
  currentPinCode: '',
  newPinCodeCode: '',
};

const useStyles = makeStyles(() => ({
  field: {
    paddingTop: '12px',
  },
}));

const EditMemberProfilePinModal = ({ onClose }: IEditCredentialsModalProps): JSX.Element => {
  const renderIntlMessage = useRenderIntlMessage();
  const dispatch = useDispatch();
  const [putProfilePin, { isLoading }] = useUpdateProfilePinMutation();

  const formMethods = useForm<IFormEditMemberPortalPinValues>({
    defaultValues: initialValues,
    resolver: yupResolver(validationSchema),
    mode: 'onBlur',
  });

  const onSubmit = async (data: IFormEditMemberPortalPinValues) => {
    try {
      await putProfilePin(data).unwrap();

      onClose();
    } catch (e) {
      dispatch(enqueueErrorNotification(e));
    }
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = formMethods;

  const classes = useStyles();
  const formId = 'update-pin-form';

  return (
    <DialogComponent
      isOpen
      size="sm"
      title={<FormattedMessage {...messages.changePinHeader} />}
      submitBtnTitle={<FormattedMessage {...messages.saveBtn} />}
      onClose={onClose}
      formId={formId}
      loading={isLoading}
    >
      <form id={formId} onSubmit={handleSubmit(onSubmit)}>
        <Grid container>
          <Grid item xs={12} sm={12}>
            <Controller
              name="currentPinCode"
              control={control}
              render={({ field }) => (
                <PinField
                  variant="outlined"
                  label={<FormattedMessage {...inputLabels.currentPin} />}
                  onBlur={field.onBlur}
                  onChange={field.onChange}
                  fullWidth
                  error={Boolean(errors.currentPinCode)}
                  helperText={renderIntlMessage(errors.currentPinCode?.message)}
                  value={field.value}
                />
              )}
            />
          </Grid>
          <Grid className={classes.field} item xs={12} sm={12}>
            <Controller
              name="newPinCode"
              control={control}
              render={({ field }) => (
                <PinField
                  variant="outlined"
                  label={<FormattedMessage {...inputLabels.newPin} />}
                  onBlur={field.onBlur}
                  onChange={field.onChange}
                  fullWidth
                  error={Boolean(errors.newPinCode)}
                  helperText={renderIntlMessage(errors.newPinCode?.message)}
                  value={field.value}
                />
              )}
            />
          </Grid>
        </Grid>
      </form>
    </DialogComponent>
  );
};

export default EditMemberProfilePinModal;
